import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Download, Printer, Mail, Bookmark, Pencil, Copy } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { eur, deDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { EmailSendDialog } from "@/components/EmailSendDialog";
import { SaveTemplateDialog } from "@/components/SaveTemplateDialog";

export default function InvoicePrint() {
  const { readOnly } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const sheetRef = useRef(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const { data: inv, isLoading } = useQuery({ queryKey: ["invoice", id], queryFn: async () => (await api.get(`/invoices/${id}`)).data });
  const { data: s } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });

  if (isLoading || !inv) return <div className="min-h-screen grid place-items-center text-slate-400 text-sm">Lädt…</div>;

  const rate = inv.kleinunternehmer ? 0 : inv.vat_rate;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Toolbar */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between">
        <button onClick={() => navigate("/invoices")} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900" data-testid="print-back">
          <ArrowLeft className="h-4 w-4" /> Zurück
        </button>
        <div className="flex items-center gap-2">
          {!readOnly && (
            <>
              <button onClick={() => navigate(`/invoices/${id}/edit`)} className="text-sm px-3.5 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-2" data-testid="print-edit" title="Bearbeiten"><Pencil className="h-4 w-4" /><span className="hidden sm:inline">Bearbeiten</span></button>
              <button onClick={() => setEmailOpen(true)} className="text-sm px-3.5 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-2" data-testid="email-invoice-button">
                <Mail className="h-4 w-4" /> <span className="hidden sm:inline">Per E-Mail</span>
              </button>
              <button onClick={() => navigate(`/invoices/new?from=${id}`)} title="Duplizieren" className="text-sm px-3.5 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-2" data-testid="print-duplicate">
                <Copy className="h-4 w-4" /> <span className="hidden sm:inline">Duplizieren</span>
              </button>
              <button onClick={() => setTplOpen(true)} className="text-sm px-3.5 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-2" data-testid="open-save-template">
                <Bookmark className="h-4 w-4" /> <span className="hidden sm:inline">Als Vorlage</span>
              </button>
            </>
          )}
          <button onClick={() => window.print()} data-testid="export-pdf-button" className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2">
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Als PDF exportieren</span><span className="sm:hidden">PDF</span>
          </button>
        </div>
      </div>

      {/* A4 sheet */}
      <div className="py-8 px-4 flex justify-center">
        <div ref={sheetRef} className="print-area bg-white w-full max-w-[210mm] min-h-[297mm] shadow-xl px-10 sm:px-14 py-12 text-slate-900" data-testid="invoice-sheet">
          {/* Header */}
          <div className="flex items-start justify-between gap-6 pb-8 border-b border-slate-200">
            <div>
              {s?.logo_url ? (
                <img src={s.logo_url} alt="Logo" className="h-12 mb-3 object-contain" />
              ) : (
                <div className="font-display text-2xl font-extrabold tracking-tight">{s?.company_name || "Ihre Firma"}</div>
              )}
              <div className="mt-2 text-xs text-slate-500 leading-relaxed">
                {s?.owner_name && <div>{s.owner_name}</div>}
                {s?.street && <div>{s.street}</div>}
                {(s?.zip || s?.city) && <div>{s?.zip} {s?.city}</div>}
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-bold tracking-tight">RECHNUNG</div>
              <div className="mt-2 font-mono text-sm font-semibold">{inv.number}</div>
              {inv.status === "storniert" && (
                <div className="mt-1 text-xs font-semibold text-red-600 uppercase tracking-widest" data-testid="print-storniert">Storniert{inv.storno_date ? ` am ${deDate(inv.storno_date)}` : ""}</div>
              )}
            </div>
          </div>

          {/* Addresses */}
          <div className="grid grid-cols-2 gap-8 mt-8">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Rechnung an</div>
              <div className="text-sm leading-relaxed">
                {inv.customer?.company_name && <div className="font-semibold">{inv.customer.company_name}</div>}
                {inv.customer?.contact && <div>{inv.customer.contact}</div>}
                {inv.customer?.street && <div>{inv.customer.street}</div>}
                {(inv.customer?.zip || inv.customer?.city) && <div>{inv.customer?.zip} {inv.customer?.city}</div>}
                {inv.customer?.vat_id && <div className="text-xs text-slate-500 mt-1">USt-IdNr.: {inv.customer.vat_id}</div>}
              </div>
            </div>
            <div className="text-sm space-y-1">
              {inv.reference && <DetailRow label={inv.reference_label || "Referenz"} value={inv.reference} />}
              <DetailRow label="Rechnungsdatum" value={deDate(inv.invoice_date)} />
              {inv.service_date && inv.service_date_end && inv.service_date_end !== inv.service_date ? (
                <DetailRow label="Leistungszeitraum" value={`${deDate(inv.service_date)} – ${deDate(inv.service_date_end)}`} />
              ) : (
                inv.service_date && <DetailRow label="Leistungsdatum" value={deDate(inv.service_date)} />
              )}
              {inv.due_date && <DetailRow label="Fällig am" value={deDate(inv.due_date)} />}
            </div>
          </div>

          {/* Items table */}
          <table className="w-full mt-10 text-sm">
            <thead>
              <tr className="border-b-2 border-blue-700 text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="py-2 font-semibold">Beschreibung</th>
                <th className="py-2 font-semibold text-right w-16">Menge</th>
                <th className="py-2 font-semibold text-right w-28">Einzelpreis</th>
                <th className="py-2 font-semibold text-right w-28">Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {inv.items?.map((it, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2.5">{it.description}</td>
                  <td className="py-2.5 text-right font-mono tabular-nums">{it.quantity}</td>
                  <td className="py-2.5 text-right font-mono tabular-nums">{eur(it.unit_price)}</td>
                  <td className="py-2.5 text-right font-mono tabular-nums">{eur(Number(it.quantity) * Number(it.unit_price))}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mt-6">
            <div className="w-72 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Nettobetrag</span><span className="font-mono tabular-nums">{eur(inv.net)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">zzgl. MwSt ({rate} %)</span><span className="font-mono tabular-nums">{eur(inv.vat_amount)}</span></div>
              <div className="flex justify-between border-t-2 border-blue-700 pt-2 mt-1">
                <span className="font-bold">Gesamtbetrag</span>
                <span className="font-mono tabular-nums text-lg font-bold" data-testid="print-gross">{eur(inv.gross)}</span>
              </div>
            </div>
          </div>

          {/* Kleinunternehmer notice */}
          {inv.kleinunternehmer && (
            <p className="mt-6 text-xs text-slate-500 leading-relaxed">
              Gemäß § 19 UStG wird keine Umsatzsteuer berechnet (Kleinunternehmerregelung).
            </p>
          )}

          {/* Note */}
          {inv.note && <p className="mt-8 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-6">{inv.note}</p>}

          {/* Footer with company details */}
          <div className="mt-auto pt-10">
            <div className="grid grid-cols-3 gap-6 text-[10px] text-slate-400 leading-relaxed border-t border-slate-200 pt-6">
              <div>
                <div className="font-semibold text-slate-500 mb-1">Kontakt</div>
                {s?.company_name && <div>{s.company_name}</div>}
                {s?.phone && <div>{s.phone}</div>}
                {s?.email && <div>{s.email}</div>}
                {s?.website && <div>{s.website}</div>}
              </div>
              <div>
                <div className="font-semibold text-slate-500 mb-1">Steuer</div>
                {s?.tax_number && <div>Steuernr.: {s.tax_number}</div>}
                {s?.vat_id && <div>USt-IdNr.: {s.vat_id}</div>}
              </div>
              <div>
                <div className="font-semibold text-slate-500 mb-1">Bankverbindung</div>
                {s?.bank_name && <div>{s.bank_name}</div>}
                {s?.iban && <div>IBAN: {s.iban}</div>}
                {s?.bic && <div>BIC: {s.bic}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="no-print text-center pb-8 text-xs text-slate-400 inline-flex items-center gap-1.5 w-full justify-center">
        <Printer className="h-3.5 w-3.5" /> Tipp: Im Druckdialog „Als PDF speichern" wählen.
      </div>

      {tplOpen && <SaveTemplateDialog inv={inv} onClose={() => setTplOpen(false)} />}
      {emailOpen && <EmailSendDialog invoiceId={id} docType="invoice" number={inv.number} onClose={() => setEmailOpen(false)} />}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3 sm:gap-6">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="font-medium text-right min-w-0">{value}</span>
    </div>
  );
}
